import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';

import { VariableInfo, WorkflowNodeType } from '@blaze-shared/api-contracts-domain';
import { ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import {
  BULK_ACTION_IDS_PROP,
  RuntimeContext,
  RuntimeEventContext,
} from '@blaze-shared/elements-domain';
import { PageContext, UrlVariable } from '@blaze-shared/projects-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { isNullishOrEmptyString } from '@blaze-shared/shared-util';
import { VariablesFacade } from '@blaze-shared/variables-data-access';
import { normalizeVarName } from '@blaze-shared/variables-util';
import {
  isDatasetNode,
  MAIN_INPUT_NAME,
  NODES_WHICH_EXECUTE_ON_FRONTEND_ONLY,
  Workflow,
  WorkflowNodeWithRowId,
} from '@blaze-shared/workflow-domain';

import { IntelliExpressionsService } from './intelli-expressions.service';

@Injectable({ providedIn: 'root' })
export class IntelliRuntimeContextService {
  private readonly iExpressionsService = inject(IntelliExpressionsService);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly variablesFacade = inject(VariablesFacade);

  /**
   * Creates context in which workflow expressions are parsed. It contains:
   * storedVars - variables stored on the server using storevariable (eg. @@my_var)
   * param workflow inputs - inputs to the given workflow (eg. $$project.id)
   * input1 - data used when evaluating $$input1
   * input2 - data used when evaluating $$input2
   */
  createRuntimeContext({
    eventContext,
    globalQueryContext,
    inputsData,
    overrideInput1,
    pageContext,
    urlVars,
  }: {
    eventContext?: RuntimeEventContext;
    globalQueryContext?: any;
    inputsData?: any;
    overrideInput1?: boolean;
    pageContext?: PageContext;
    urlVars?: UrlVariable[];
  }): RuntimeContext {
    inputsData = inputsData || {};
    if (overrideInput1) {
      console.log(`Setting input1 and form to:`, pageContext?.data);
      inputsData = { ...inputsData, input1: pageContext?.data || {} };
    }
    const account = this.projectContextFacade.varAccount;
    let context: RuntimeContext = {
      ...inputsData,
      $$Account: account?.Name ? account : {},
      $$Page: pageContext?.data || {},
      $$User: this.projectContextFacade.varUser || {},
      $$Url: {},
      storedVars: {},
      storedVarsFullInfo: {},
    };

    if (eventContext) {
      for (const key of [BULK_ACTION_IDS_PROP] as const) {
        if (context[key]) {
          console.error(
            `EventContext[${key}] already exists in the context. The value is overwritten.`,
          );
        }
        context[key] = eventContext[key];
      }
    }

    const variables = this.variablesFacade.varInfos();
    for (const varInfo of variables) {
      const variableName = normalizeVarName(varInfo.name);
      if (varInfo?.value !== 'undefined') {
        context.storedVars[variableName] = varInfo.value;
        context.storedVarsFullInfo[variableName] = varInfo;
      }
    }

    if (urlVars) {
      for (const urlVar of urlVars) {
        context['$$Url'][urlVar.name] = urlVar.value;
      }
    }

    if (globalQueryContext) {
      context = {
        ...context,
        ...globalQueryContext,
      };
    }

    return context;
  }

  getLocalVarsForResume(projectId?: string): Record<string, VariableInfo> {
    const varInfos = this.variablesFacade
      .varInfos()
      .filter((v) => v.scope === 'Local' && v.projectId === projectId);
    const localVarsForResume: Record<string, VariableInfo> = {};
    for (const localVar of varInfos) {
      const localVarName = normalizeVarName(localVar.name);
      localVarsForResume[localVarName] = localVar.value;
    }
    return localVarsForResume;
  }

  prepareRuntimeContextForServer(ctx: RuntimeContext, workflow: Workflow): { [key: string]: any } {
    ctx = this.iExpressionsService.normalizeLinkedParams(ctx);

    let context: { [key: string]: any } = {};
    for (const key in ctx) {
      if (Object.hasOwn(ctx, key)) {
        if (key === 'storedVarsFullInfo') {
          // do not include in the server payload
          continue;
        }
        if (key === 'storedVars') {
          for (const variableName in ctx[key]) {
            const varName = normalizeVarName(variableName);
            if (Object.hasOwn(ctx[key], varName)) {
              context[variableName] = ctx['storedVars'][varName];
            }
          }
        } else if (!key.startsWith('$$')) {
          context[`$$${key}`] = ctx[key];
        } else {
          context[key] = ctx[key];
        }
      }
    }

    const rowId = this.getCurrentRowId(workflow, ctx);
    context = !isNullishOrEmptyString(rowId) ? { ...context, rowId } : context;

    // Server uses externalAccountRowId to infer $$Account. $$User is being passed in case an editor previews
    // project as a different user.
    if (context) {
      delete context['$$Account'];
    }

    return context;
  }

  updateRuntimeContext(
    context: RuntimeContext,
    input1Data?: Dictionary<any> | null,
    input2Data?: Dictionary<any> | null,
  ): RuntimeContext {
    context = { ...context, input1: input1Data || {} };
    if (input2Data && context['input2']) {
      console.error(
        `Unable to merge inputs. input2 already exists in inputs. Workflow might not work properly`,
      );
    } else if (input2Data) {
      context = { ...context, input2: input2Data };
    }
    return context;
  }

  /**
   * Updates context with default values of stored variables. Used only with frontend-only nodes,
   * to provide support for default values.
   */
  updateRuntimeContextWithStoredVarsDefaults(
    ctx: RuntimeContext,
    nodeType: WorkflowNodeType,
  ): RuntimeContext {
    if (!NODES_WHICH_EXECUTE_ON_FRONTEND_ONLY.has(nodeType)) {
      return ctx;
    }

    const varDefs = this.variablesFacade.varDefs();
    const storedVars = { ...ctx.storedVars };

    for (const varDef of varDefs) {
      if (varDef.defaultValue && !storedVars[varDef.name]) {
        storedVars[varDef.name] = varDef.defaultValue;
      }
    }

    return { ...ctx, storedVars };
  }

  updateRuntimeContextNamedOutput(
    context: RuntimeContext,
    input1Data: Dictionary<any> | null,
    outputName?: string,
  ): RuntimeContext {
    if (!outputName || outputName === '$$input1') {
      return context;
    }
    const inputName = outputName.replaceAll('$$', ''); // normalize
    const data = Array.isArray(input1Data) && input1Data?.length === 1 ? input1Data[0] : input1Data;
    return { ...context, [inputName]: data ?? {} };
  }

  /**
   * Finds and returns a rowId associated with a dataset's "current row" action.
   */
  private getCurrentRowId(workflow: Workflow, context: RuntimeContext): string | undefined {
    const currentRowNode = workflow?.nodes?.find(
      (node) => (node as WorkflowNodeWithRowId).currentRow,
    );
    if (isDatasetNode(currentRowNode)) {
      const idCol = this.schemaService.getIdColumnName(currentRowNode.datasetId);
      return (
        context?.['form']?.[idCol] ||
        context?.['detailsView']?.[idCol] ||
        context?.[MAIN_INPUT_NAME]?.[idCol]
      );
    }
    return;
  }
}
